// Which persona is looking at the evaluation queue — the AUDITOR browsing every
// captured case, or the REVIEWER working an assigned queue.
//
// THE UI REFLECTS THE SERVER'S DECISION; IT NEVER MAKES ONE.
//
// The server is the authority. services/evalreview/scope.go resolves scope per
// request: a caller holding one of the all-batches role names gets
// Scope{All:true}, and everyone else gets the batches their queue membership
// names. Nothing here can widen that — the batch list this label sits above has
// already been filtered server-side, and the label is a description of what
// arrived, not a permission.
//
// So the failure mode this file guards against is not privilege escalation, it
// is a LIE ABOUT COMPLETENESS. Telling a queue-scoped reviewer they are seeing
// "all captured cases" makes a filtered list read as the whole corpus, and work
// that is missing from their queue looks like work that does not exist. That is
// why the all-batches claim is made ONLY on an exact match against the same
// role names the server matches, and why an unrecognised or absent role claims
// nothing at all rather than defaulting to either persona.

/**
 * The role names that confer the all-batches capability server-side.
 *
 * MUST match `allBatchesRoles` in platform/services/evalreview/scope.go. These
 * are IdP group strings copied verbatim out of the token (underscores), not
 * authz builtin role ids (hyphens) — a distinction that has bitten before.
 *
 * Pinned by evalPersona.test.ts.
 */
export const ALL_BATCHES_ROLES: readonly string[] = ["eval_manager", "auditor"];

export type EvalPersona =
  /** Oversight: every captured case, read-only. Server returned Scope{All}. */
  | "all-batches"
  /** Reviewer: the batches their queue names. */
  | "assigned-queue"
  /** No principal, or a role the server's rule does not recognise. */
  | "unknown";

/**
 * Resolve the persona from the roles the SERVER reported for the session.
 *
 * ANY matching role wins, which is exactly how the server resolves it —
 * allBatchesRoleOf in scope.go scans the caller's whole role list and returns
 * the first that confers the capability. A principal holds several roles
 * routinely, because roles are IdP group strings: a team's directory group
 * alongside whatever carries the grant.
 *
 * This read the SINGLE role the session used to report (principal.Roles[0])
 * until 2026-08-07, so ["viewer","auditor"] arrived as "viewer" and an auditor
 * was labelled a queue-scoped reviewer. That understated access, which was the
 * safe direction and the reason it survived — but it made the label disagree
 * with the server about the caller's own scope, and the obvious workaround was
 * the dangerous one: inferring the persona from the DATA ("lots of batches ⇒
 * auditor"), which invents an answer the server never gave. The fix was the
 * session endpoint reporting the full set; this consumes it.
 *
 * Still cannot widen anything. The batch list this label sits above was already
 * filtered server-side; naming a persona the server would not have granted only
 * produces a wrong sentence, never an extra case.
 */
export function evalPersonaForRoles(
  roles: readonly string[] | null | undefined,
): EvalPersona {
  if (!Array.isArray(roles)) return "unknown";
  // Trim first, then drop the empties: a payload of [""] or ["  "] has told us
  // nothing, and must land on "unknown" rather than on the reviewer default.
  const named = roles
    .filter((r): r is string => typeof r === "string")
    .map((r) => r.trim())
    .filter(Boolean);
  if (named.length === 0) return "unknown";
  // Exact, whole-string, case-sensitive — matching how the server matches.
  // Case folding or prefix matching would let an unrelated group
  // ("Auditors-EMEA") land on the all-batches label.
  if (named.some((r) => ALL_BATCHES_ROLES.includes(r))) return "all-batches";
  return "assigned-queue";
}

/**
 * Single-role convenience over evalPersonaForRoles.
 *
 * Kept because one role is a real shape — `user.role`, the clamped legacy field
 * — and because the near-miss cases are easier to state one string at a time.
 * It DELEGATES rather than reimplementing: two matchers is how the UI's rule
 * and the server's drift apart one edge case at a time.
 */
export function evalPersonaForRole(role: string | null | undefined): EvalPersona {
  if (typeof role !== "string") return "unknown";
  return evalPersonaForRoles([role]);
}
